import {
  BiomassBenchmarkChartModel,
  BiomassBenchmarkChartSeries,
  BiomassBenchmarkDataset,
  BiomassBenchmarkNamedSeries,
  BiomassBenchmarkPoint,
  BiomassComparableFieldSeries,
} from './biomass-benchmark.models';
import moment from 'moment';

const WINDOW_START_MONTH_INDEX = 3;
const WINDOW_START_DAY = 1;
const WINDOW_END_MONTH_INDEX = 8;
const WINDOW_END_DAY = 1;

interface MapBiomassBenchmarkToChartModelParams {
  dataset: BiomassBenchmarkDataset;
  fieldSeriesName: string;
  benchmarkSeriesName: string;
}

export function normalizeBiomassBenchmarkPoints(points: BiomassBenchmarkPoint[]): BiomassBenchmarkPoint[] {
  return normalizeAndSortPoints(points);
}

export function mapBiomassBenchmarkToChartModel({
  dataset,
  fieldSeriesName,
  benchmarkSeriesName,
}: MapBiomassBenchmarkToChartModelParams): BiomassBenchmarkChartModel | null {
  const fieldSeries = dataset.fieldSeries
    ? dataset.fieldSeries.map(toNamedSeries).filter(isUsableSeries)
    : [toChartSeries(fieldSeriesName, normalizeBiomassBenchmarkPoints(dataset.points), true)];
  const hasFieldSeries = fieldSeries.some((series) => series.data.some(([, value]) => value !== null));

  const comparableSeries = dataset.benchmarkSeries
    ? dataset.benchmarkSeries.map(toNamedSeries).filter(isUsableSeries)
    : mapComparableSeries(dataset, benchmarkSeriesName);
  const hasBenchmarkSeries = comparableSeries.length > 0;
  if (!hasFieldSeries && !hasBenchmarkSeries) {
    return null;
  }

  return {
    fieldSeries,
    comparableSeries,
    hasFieldSeries,
    hasBenchmarkSeries,
  };
}

function normalizeAndSortPoints(points: BiomassBenchmarkPoint[]): BiomassBenchmarkPoint[] {
  const normalizedByDate = new Map<string, BiomassBenchmarkPoint>();

  for (const point of points) {
    const parsedDate = parseUtcMidnight(point.dateUtc);
    if (!parsedDate || !isWithinChartWindow(parsedDate)) {
      continue;
    }

    const dateKey = toDateKey(parsedDate);
    normalizedByDate.set(dateKey, {
      dateUtc: `${dateKey}T00:00:00.000Z`,
      value: clampValue(point.value),
    });
  }

  return [...normalizedByDate.entries()]
    .sort(([leftDate], [rightDate]) => leftDate.localeCompare(rightDate))
    .map(([, value]) => value);
}

function toChartData(points: BiomassBenchmarkPoint[]): Array<[number, number | null]> {
  return points.map((point): [number, number | null] => [Date.parse(point.dateUtc), point.value]);
}

function mapComparableSeries(dataset: BiomassBenchmarkDataset, benchmarkSeriesName: string) {
  const explicitComparableFields = dataset.comparableCropFields?.length ? dataset.comparableCropFields : null;

  if (explicitComparableFields) {
    const mappedComparableSeries = explicitComparableFields
      .map((series) => toComparableSeries(series))
      .filter((series) => series !== null);
    if (mappedComparableSeries.length > 0) {
      return mappedComparableSeries.map((series, index) => ({
        ...series,
        name: benchmarkSeriesName,
        showInLegend: index === 0,
      }));
    }
  }

  return [];
}

function toComparableSeries(series: BiomassComparableFieldSeries) {
  const normalizedPoints = normalizeAndSortPoints(series.points);
  const sortedData = toChartData(normalizedPoints);

  if (!sortedData.some(([, value]) => value !== null)) {
    return null;
  }

  return {
    name: '',
    data: sortedData,
    showInLegend: false,
  };
}

function toNamedSeries(series: BiomassBenchmarkNamedSeries): BiomassBenchmarkChartSeries | null {
  const normalizedPoints = normalizeAndSortPoints(series.points);
  if (!toChartData(normalizedPoints).some(([, value]) => value !== null)) {
    return null;
  }

  return toChartSeries(series.name, normalizedPoints, true);
}

function toChartSeries(
  name: string,
  points: BiomassBenchmarkPoint[],
  showInLegend: boolean,
): BiomassBenchmarkChartSeries {
  return {
    name,
    data: toChartData(points),
    showInLegend,
  };
}

function isUsableSeries(series: BiomassBenchmarkChartSeries | null): series is BiomassBenchmarkChartSeries {
  return series !== null;
}

function parseUtcMidnight(dateUtc: string): moment.Moment | null {
  const parsedDate = moment.utc(dateUtc, moment.ISO_8601, true);
  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.startOf('day');
}

function isWithinChartWindow(dateUtc: moment.Moment): boolean {
  const windowStart = dateUtc.clone().month(WINDOW_START_MONTH_INDEX).date(WINDOW_START_DAY).startOf('day');
  const windowEnd = dateUtc.clone().month(WINDOW_END_MONTH_INDEX).date(WINDOW_END_DAY).startOf('day');

  return dateUtc.isSameOrAfter(windowStart, 'day') && dateUtc.isSameOrBefore(windowEnd, 'day');
}

function clampValue(value: number | null): number | null {
  if (value === null || Number.isNaN(value)) {
    return null;
  }

  if (value < 0) {
    return 0;
  }

  if (value > 1) {
    return 1;
  }

  return value;
}

function toDateKey(dateUtc: moment.Moment): string {
  return dateUtc.format('YYYY-MM-DD');
}
